import React, { useState, ChangeEvent, FormEvent } from 'react';
import { sendEmail } from '../../api-client'; // Adjust the path as needed
import { ContactFormData } from '../../../../backend/src/shared/types'; // Import the new type

interface FormErrors {
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  message?: string;
}

const ContactUsForm: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    fullName: '',
    email: '',
    phoneNumber: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false); // New loading state

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: undefined
      }));
    }
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
    } else {
      setLoading(true);
      try {
        // Send email using API client function
        await sendEmail(formData);
        alert('Message sent successfully!');
        setFormData({
          fullName: '',
          email: '',
          phoneNumber: '',
          message: ''
        });
      } catch (error) {
        alert('Failed to send message');
        console.error('Error sending message:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <form className="flex flex-col gap-5 px-10 lg:px-0" onSubmit={handleSubmit}>
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center mt-5 md:mt-0">Send a message</h2>
      <label className="block text-gray-700 text-sm font-bold">
        Full Name
        <input
          name="fullName"
          className="border rounded w-full py-2 px-3 mt-1 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          value={formData.fullName}
          onChange={handleChange}
          disabled={loading}
        />
        {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
      </label>
      <label className="block text-gray-700 text-sm font-bold">
        Email
        <input
          type="email"
          name="email"
          className="border rounded w-full py-2 px-3 mt-1 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          value={formData.email}
          onChange={handleChange}
          disabled={loading}
        />
        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
      </label>
      <label className="block text-gray-700 text-sm font-bold">
        Phone Number
        <input
          name="phoneNumber"
          className="border rounded w-full py-2 px-3 mt-1 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          value={formData.phoneNumber}
          onChange={handleChange}
          disabled={loading}
        />
        {errors.phoneNumber && <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>}
      </label>
      <label className="block text-gray-700 text-sm font-bold">
        Message
        <textarea
          name="message"
          className="border rounded w-full py-2 px-3 mt-1 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          value={formData.message}
          onChange={handleChange}
          disabled={loading}
        />
        {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
      </label>
      <span>
        <button
          type="submit"
          className={`bg-halo-pink text-white py-2 px-6 rounded hover:bg-pink-800 focus:outline-none focus:shadow-outline mb-5 md:mb-0 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={loading}
        >
          {loading ? 'Sending...' : 'Send Message'}
        </button>
      </span>
    </form>
  );
};

export default ContactUsForm;
